import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  Target, 
  TrendingUp, 
  Clock,
  CheckCircle,
  Circle,
  Pause,
  XCircle,
  Edit,
  Trash2,
  MoreVertical,
  Share2
} from 'lucide-react';
import type { Outcome } from '../../types/outcomes';

interface OutcomeCardProps {
  outcome: Outcome;
  onClick?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onShare?: () => void;
}

export const OutcomeCard: React.FC<OutcomeCardProps> = ({ outcome, onClick, onEdit, onDelete, onShare }) => {
  const [showActions, setShowActions] = React.useState(false);
  const getStatusIcon = () => {
    switch (outcome.status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-sage-600" />;
      case 'in-progress':
        return <TrendingUp className="h-5 w-5 text-primary-600" />;
      case 'planning':
        return <Circle className="h-5 w-5 text-charcoal-400" />;
      case 'on-hold':
        return <Pause className="h-5 w-5 text-[var(--mustard)]" />;
      case 'cancelled':
        return <XCircle className="h-5 w-5 text-coral-600" />;
    }
  };

  const getStatusColor = () => {
    switch (outcome.status) {
      case 'completed':
        return 'bg-sage-100 text-sage-800 border-sage-200';
      case 'in-progress':
        return 'bg-primary-100 text-primary-800 border-primary-200';
      case 'planning':
        return 'bg-charcoal-100 text-charcoal-800 border-charcoal-200';
      case 'on-hold':
        return 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]';
      case 'cancelled':
        return 'bg-coral-100 text-coral-800 border-coral-200';
    }
  };

  const getCategoryColor = () => {
    switch (outcome.category) {
      case 'strategic':
        return 'bg-primary-50 text-primary-700';
      case 'operational':
        return 'bg-sage-50 text-sage-700';
      case 'team':
        return 'bg-[var(--mustard-dim)] text-[var(--mustard)]';
      case 'personal':
        return 'bg-coral-50 text-coral-700';
      case 'project':
        return 'bg-charcoal-50 text-charcoal-700';
    }
  };

  const getImpactBadge = () => {
    const colors = {
      low: 'bg-charcoal-100 text-charcoal-700',
      medium: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
      high: 'bg-coral-100 text-coral-700',
      transformational: 'bg-primary-100 text-primary-700',
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors[outcome.impactLevel]}`}>
        {outcome.impactLevel.charAt(0).toUpperCase() + outcome.impactLevel.slice(1)} Impact
      </span>
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <div
      onClick={onClick}
      className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:shadow-md transition-shadow cursor-pointer"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3 flex-1">
          {getStatusIcon()}
          <div className="flex-1">
            <h3 className="text-lg font-bold text-charcoal-900 mb-1">{outcome.title}</h3>
            <p className="text-sm text-charcoal-600 line-clamp-2">{outcome.description}</p>
          </div>
        </div>
        
        {/* Action Menu */}
        {(onEdit || onDelete) && (
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowActions(!showActions);
              }}
              className="p-2 hover:bg-[var(--hover-tint)] transition-colors"
              aria-label="More actions"
            >
              <MoreVertical className="h-5 w-5 text-charcoal-600" />
            </button>
            {showActions && (
              <div className="absolute right-0 mt-2 w-48 bg-[var(--bg-raised)] shadow-lg border border-[var(--rule)] py-1 z-10">
                {onShare && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowActions(false);
                      onShare();
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-coral-700 hover:bg-coral-50 flex items-center gap-2"
                  >
                    <Share2 className="h-4 w-4" />
                    Share as Story
                  </button>
                )}
                {onEdit && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowActions(false);
                      onEdit();
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-charcoal-700 hover:bg-[var(--hover-tint)] flex items-center gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    Edit Outcome
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowActions(false);
                      onDelete();
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-charcoal-700 hover:bg-[var(--hover-tint)] flex items-center gap-2 border-t border-[var(--rule)]"
                  >
                    <Trash2 className="h-4 w-4 text-coral-600" />
                    <span className="text-coral-700">Delete Outcome</span>
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Badges */}
      <div className="flex flex-wrap gap-2 mb-4">
        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor()}`}>
          {outcome.status.replace('-', ' ').toUpperCase()}
        </span>
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor()}`}>
          {outcome.category.charAt(0).toUpperCase() + outcome.category.slice(1)}
        </span>
        {getImpactBadge()}
      </div>

      {/* Progress Bar */}
      {outcome.status !== 'cancelled' && (
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-charcoal-600">Progress</span>
            <span className="font-semibold text-charcoal-900">{outcome.progressPercentage}%</span>
          </div>
          <div className="h-2 bg-[var(--hover-tint)] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary-400 to-sage-400 transition-all duration-500"
              style={{ width: `${outcome.progressPercentage}%` }}
            />
          </div>
        </div>
      )}

      {/* Milestones */}
      {outcome.milestones.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-2 text-sm text-charcoal-600">
            <Target className="h-4 w-4" />
            <span>
              {outcome.milestones.filter(m => m.status === 'completed').length} / {outcome.milestones.length} milestones
            </span>
          </div>
        </div>
      )}

      {/* Frameworks Applied */}
      {outcome.frameworkApplications.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {outcome.frameworkApplications.slice(0, 3).map((app, idx) => (
            <Link
              key={idx}
              to={`/elevation/entry/${app.frameworkId}`}
              onClick={(e) => e.stopPropagation()}
              className="text-xs px-2 py-1 bg-primary-50 text-primary-700 rounded hover:bg-primary-100 transition-colors"
            >
              {app.frameworkName}
            </Link>
          ))}
          {outcome.frameworkApplications.length > 3 && (
            <span className="text-xs px-2 py-1 bg-[var(--hover-tint)] text-charcoal-600 rounded">
              +{outcome.frameworkApplications.length - 3} more
            </span>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-charcoal-500 pt-4 border-t border-[var(--rule)]">
        <div className="flex items-center gap-4">
          {outcome.targetDate && (
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span>{formatDate(outcome.targetDate)}</span>
            </div>
          )}
          {outcome.completedDate && (
            <div className="flex items-center gap-1">
              <CheckCircle className="h-3 w-3 text-sage-600" />
              <span>Completed {formatDate(outcome.completedDate)}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          <span>Updated {formatDate(outcome.lastUpdated)}</span>
        </div>
      </div>
    </div>
  );
};

